import { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Popover,
  Paper,
  InputBase,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

const pad = (n: number) => String(n).padStart(2, "0");

type variant = "standard" | "outlined" | "filled";

interface MinTime {
  hour: number;   // 24-hour
  minute: number;
}

interface TimePickerProps {
  field: {
    value?: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    name?: string;
  };
  label?: string;
  isDisabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  isFullTimeRequired?: boolean;
  minTime?: MinTime; // restrict past times when today is selected
  size?: "small" | "medium";
  variant?: variant;
  sx?: any;
}

type Period = "AM" | "PM";

interface ParsedTime {
  hour: number;
  minute: number;
  period: Period;
}

/** Convert 12-hour tempHour + tempPeriod to 24-hour integer */
const to24 = (hour: number, period: Period): number => {
  if (period === "AM") {
    return hour === 12 ? 0 : hour;
  }

  return hour === 12 ? 12 : hour + 12;
};

// Convert a 24-hour time to 12-hour picker values.
const from24 = (
  hour24: number,
  minute: number
): ParsedTime => ({
  hour: hour24 % 12 || 12,
  minute,
  period: hour24 >= 12 ? "PM" : "AM",
});

// Parse both 12-hour and 24-hour time strings.
const parseTime = (value?: string): ParsedTime | null => {
  if (!value?.trim()) return null;

  const match = value.trim().match(
    /^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i
  );

  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const period = match[3]?.toUpperCase() as Period | undefined;

  if (minutes < 0 || minutes > 59) return null;

  if (period) {
    if (hours < 1 || hours > 12) return null;

    return {
      hour: hours,
      minute: minutes,
      period,
    };
  }

  if (hours < 0 || hours > 23) return null;

  return from24(hours, minutes);
};

const TimePicker = ({
  field,
  label,
  isDisabled,
  required = false,
  error = false,
  helperText,
  isFullTimeRequired = true,
  minTime,
  size = "small",
  variant = "standard",
  sx,
}: TimePickerProps) => {
  const { value, onChange, onBlur } = field;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tempHour, setTempHour] = useState<string>("");
  const [tempMinute, setTempMinute] = useState<string>("");
  const [tempPeriod, setTempPeriod] = useState<Period>("AM");

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!anchorEl) return;

    const parsed = parseTime(value);

    if (parsed) {
      setTempHour(pad(parsed.hour));
      setTempMinute(pad(parsed.minute));
      setTempPeriod(parsed.period);
    } else if (value) {
      setTempHour("");
      setTempMinute("");
      setTempPeriod("AM");
    } else {
      const now = new Date();
      const parsedNow = from24(
        now.getHours(),
        now.getMinutes()
      );

      setTempHour(pad(parsedNow.hour));
      setTempMinute(pad(parsedNow.minute));
      setTempPeriod(parsedNow.period);
    }
  }, [anchorEl, value]);

  const getCurrent24Hour = () => to24(Number(tempHour), tempPeriod);

  const isBelowMinTime = (hour24 = getCurrent24Hour(), minute = Number(tempMinute)): boolean => {
    if (!minTime) return false;

    if (hour24 < minTime.hour) return true;

    if (hour24 === minTime.hour && minute < minTime.minute) {
      return true;
    }

    return false;
  };

  // Apply a time and keep hour, minute, and AM/PM synchronized.
  const applyTime = (hour24: number, minute: number) => {
    const next = from24(hour24, minute);

    setTempHour(pad(next.hour));
    setTempMinute(pad(next.minute));
    setTempPeriod(next.period);
  };

  const clampToMinTime = (hour24: number, minute: number): { hour: number; minute: number } => {
    if (!minTime) return { hour: hour24, minute };
    if (hour24 < minTime.hour) return { hour: minTime.hour, minute: minTime.minute };
    if (hour24 === minTime.hour && minute < minTime.minute) return { hour: hour24, minute: minTime.minute };
    return { hour: hour24, minute };
  };

  const handleHourUp = () => {
    if (!tempHour || !tempMinute) return;

    const currentHour = Number(tempHour);
    const newHour = currentHour >= 12 ? 1 : currentHour + 1;

    const newPeriod = currentHour === 11 ? tempPeriod === "AM" ? "PM" : "AM" : tempPeriod;

    const newHour24 = to24(newHour, newPeriod);
    const clamped = clampToMinTime(newHour24, Number(tempMinute));
    applyTime(clamped.hour, clamped.minute);
  };

  const handleHourDown = () => {
    if (!tempHour || !tempMinute) return;

    const currentHour = Number(tempHour);
    const newHour = currentHour <= 1 ? 12 : currentHour - 1;

    const newPeriod = currentHour === 12 ? tempPeriod === "AM" ? "PM" : "AM" : tempPeriod;

    const newHour24 = to24(newHour, newPeriod);
    const clamped = clampToMinTime(newHour24, Number(tempMinute));
    applyTime(clamped.hour, clamped.minute);
  };

  const handleMinuteUp = () => {
    if (!tempHour || !tempMinute) return;

    const currentHour24 = getCurrent24Hour();
    const currentMinute = Number(tempMinute);

    let nextHour = currentHour24;
    let nextMinute = currentMinute + 1;

    if (nextMinute > 59) {
      nextMinute = 0;
      nextHour = (currentHour24 + 1) % 24;
    }

    const clamped = clampToMinTime(nextHour, nextMinute);
    applyTime(clamped.hour, clamped.minute);
  };

  const handleMinuteDown = () => {
    if (!tempHour || !tempMinute) return;

    const currentHour24 = getCurrent24Hour();
    const currentMinute = Number(tempMinute);

    let nextHour = currentHour24;
    let nextMinute = currentMinute - 1;

    if (nextMinute < 0) {
      nextMinute = 59;
      nextHour = (currentHour24 + 23) % 24;
    }

    const clamped = clampToMinTime(nextHour, nextMinute);
    applyTime(clamped.hour, clamped.minute);
  };

  const handlePeriodToggle = () => {
    if (!tempHour || !tempMinute) return;

    const newPeriod: Period = tempPeriod === "AM" ? "PM" : "AM";
    const newHour24 = to24(Number(tempHour), newPeriod);
    const clamped = clampToMinTime(newHour24, Number(tempMinute));
    applyTime(clamped.hour, clamped.minute);
  };

  const handleDone = () => {
    const hour = Number(tempHour);
    const minute = Number(tempMinute);

    if (
      !tempHour ||
      !tempMinute ||
      !Number.isInteger(hour) ||
      hour < 1 ||
      hour > 12 ||
      !Number.isInteger(minute) ||
      minute < 0 ||
      minute > 59
    ) {
      return;
    }

    const hour24 = to24(hour, tempPeriod);

    if (isBelowMinTime(hour24, minute)) return;

    let time: string;

    if (isFullTimeRequired) {
      time = `${pad(hour)}:${pad(minute)} ${tempPeriod}`;
    } else {
      time = `${pad(hour24)}:${pad(minute)}`;
    }

    onChange(time);
    setAnchorEl(null);
    onBlur?.();
  };

  const handleClose = () => {
    onBlur?.();
    setAnchorEl(null);
  };

  return (
    <>
      <TextField
        size={size}
        variant={variant}
        fullWidth
        label={label}
        placeholder={isFullTimeRequired ? "HH:MM AM/PM" : "HH:MM"}
        value={value || ""}
        error={error}
        required={required}
        disabled={isDisabled}
        helperText={helperText}
        onClick={(e) => {
          if (!isDisabled) {
            setAnchorEl(e.currentTarget);
          }
        }}
        slotProps={{
          formHelperText: {
            sx: {
              position: "absolute",
              top: "100%",
              left: 0,
              margin: 0,
              fontSize: "10px",
              lineHeight: "12px",
              whiteSpace: "nowrap",
            },
          },
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();

                    if (!isDisabled) {
                      setAnchorEl(e.currentTarget.parentElement?.parentElement || e.currentTarget);
                    }
                  }}
                  edge="end"
                  sx={{ padding: "4px" }}
                >
                  <AccessTimeOutlinedIcon
                    sx={{
                      fontSize: 18,
                      color: error ? "#d32f2f" : "#6b7280",
                    }}
                  />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        sx={{
          position: "relative",
          "& .MuiInputBase-root": {
            fontSize: "11.5px",
          },
          "& .MuiOutlinedInput-root": {
            height: "40px",
            minHeight: "40px",
            paddingRight: "6px",
            borderRadius: "6px",
          },
          "& .MuiOutlinedInput-input": {
            padding: "8px",
            height: "100%",
            boxSizing: "border-box",
          },
          "& .MuiInputBase-input": {
            cursor: "pointer",
          },
          // Position the floating label correctly.
          "& .MuiInputLabel-outlined": {
            transform: "translate(14px, 10px) scale(1)",
            fontSize: "12px",
          },

          "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
            transform: "translate(14px, -7px) scale(0.85)",
            backgroundColor: "#fff",
            padding: "0 4px",
            zIndex: 1,
          },
          "& .MuiSvgIcon-root": {
            fontSize: 18,
            color: error ? "#d32f2f" : "#6b7280",
          },
          "& .MuiInput-underline:before": {
            borderBottom: error ? "1px solid #d32f2f" : "1px solid rgba(0, 0, 0, 0.42)",
          },
          "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
            borderBottom: error ? "1px solid #d32f2f" : "1px solid rgba(0, 0, 0, 0.42)",
          },
          "& .MuiInput-underline:after": {
            borderBottomColor: error ? "#d32f2f" : "#5145e5",
          },
          ...sx,
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Paper
          sx={{
            p: 2,
            width: 220,
            borderRadius: 2,
            boxShadow: "0px 4px 12px rgba(0,0,0,0.15)"
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            mb={1}
          >
            {/* Hour */}
            <Box display="flex" flexDirection="column" alignItems="center">
              <IconButton
                onClick={handleHourUp}
                size="small" sx={{ height: 32, width: 32 }}
              >
                <KeyboardArrowUpIcon color="primary" />
              </IconButton>

              <Box
                sx={{
                  border: "1px solid #d0d7e2",
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.8,
                  minWidth: 48,
                  textAlign: "center"
                }}
              >
                <InputBase
                  value={tempHour}
                  placeholder="HH"
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    if (v === "" || (Number(v) >= 1 && Number(v) <= 12)) {
                      setTempHour(v);
                    }
                  }}
                  inputProps={{
                    maxLength: 2,
                    inputMode: "numeric",
                    style: { textAlign: "center" },
                  }}
                  sx={{ width: 32 }}
                />
              </Box>

              <IconButton
                size="small"
                onClick={handleHourDown}
                sx={{ height: 32, width: 32 }}
              >
                <KeyboardArrowDownIcon color="primary" />
              </IconButton>
            </Box>

            <Typography variant="h6">:</Typography>

            {/* Minute */}
            <Box display="flex" flexDirection="column" alignItems="center">
              <IconButton
                size="small"
                onClick={handleMinuteUp}
                sx={{ height: 32, width: 32 }}
              >
                <KeyboardArrowUpIcon color="primary" />
              </IconButton>

              <Box
                sx={{
                  border: "1px solid #d0d7e2",
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.8,
                  minWidth: 48,
                  textAlign: "center"
                }}
              >
                <InputBase
                  value={tempMinute}
                  placeholder="MM"
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    if (v === "" || Number(v) <= 59) {
                      setTempMinute(v);
                    }
                  }}
                  inputProps={{
                    maxLength: 2,
                    inputMode: "numeric",
                    style: { textAlign: "center" },
                  }}
                  sx={{ width: 32 }}
                />
              </Box>

              <IconButton
                size="small"
                onClick={handleMinuteDown}
                sx={{ height: 32, width: 32 }}
              >
                <KeyboardArrowDownIcon color="primary" />
              </IconButton>
            </Box>

            {/* AM / PM */}
            <Button
              variant="outlined"
              size="small"
              onClick={handlePeriodToggle}
              sx={{
                border: "1px solid #d0d7e2",
                minWidth: 55,
                height: 40,
              }}
            >
              {tempPeriod}
            </Button>
          </Box>

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              size="small"
              onClick={handleDone}
              disabled={isBelowMinTime()}
            >
              Done
            </Button>
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default TimePicker;